import React, { useState, useEffect, ChangeEvent, FocusEvent } from 'react';
import { formatCurrencyInput, parseCurrency, validateCurrencyAmount } from '../utils/currency';

interface CurrencyInputProps {
  id?: string;
  name: string;
  value: number | string;
  onChange: (value: number, formattedValue: string) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  className?: string;
  label?: string;
  error?: string;
  showLabel?: boolean;
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder = '0.00',
  disabled = false,
  required = false,
  min,
  max,
  className = '',
  label,
  error,
  showLabel = true,
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Initialize display value from prop value
  useEffect(() => {
    if (!isFocused) {
      const numValue = typeof value === 'string' ? parseCurrency(value) : value;
      if (numValue > 0) {
        setDisplayValue(formatCurrencyInput(numValue.toString()));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Format the input as user types
    const formatted = formatCurrencyInput(inputValue, displayValue);
    setDisplayValue(formatted);

    // Parse back to number and call onChange
    const numericValue = parseCurrency(formatted);
    onChange(numericValue, formatted);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    // Validate on blur if min/max provided
    if (min !== undefined || max !== undefined) {
      const numValue = parseCurrency(displayValue);
      validateCurrencyAmount(numValue, min, max);
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="form-group">
      {showLabel && label && (
        <label htmlFor={id || name}>
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <div className="currency-input-wrapper">
        <span className="currency-symbol">₦</span>
        <input
          type="text"
          id={id || name}
          name={name}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`currency-input ${className} ${error ? 'error' : ''}`}
          inputMode="decimal"
          autoComplete="off"
        />
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
};
